import {
  UrgentDeliveryManagementPODData,
  UrgentDeliveryManagementPODList,
  UrgentDeliveryManagementPODListRequest,
  UrgentDeliveryManagementRTSData,
  UrgentDeliveryManagementRTSList,
  UrgentDeliveryManagementRTSListRequest,
  UrgentDeliveryManagementHALData,
  UrgentDeliveryManagementHALList,
  UrgentDeliveryManagementHALListRequest,
  UrgentDeliveryManagementPODListRequestDownload,
  UrgentDeliveryManagementHALListRequestDownload,
  UrgentDeliveryManagementRTSListRequestDownload,
  UrgentDeliveryManagementPODSummaryData,
  UrgentDeliveryManagementPODSummaryRequest,
  UrgentDeliveryManagementRelabelList,
  UrgentDeliveryManagementRelabelListRequest,
  UrgentDeliveryManagementRelabelListRequestDownload,
  LastLocationOption
} from "@/domain/entities/UrgentDeliveryManagement";
import {
  Action,
  Module,
  Mutation,
  VuexModule,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import { container } from "tsyringe";
import { UrgentDeliveryManagementPresenter } from "../presenters/UrgentDeliveryManagementPresenter";
import { Pagination } from "@/domain/entities/Pagination";
import { MainAppController } from "./MainAppController";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import { AccountController } from "./AccountController";
import {
  parsingErrorResponse,
  storeDatatoLocalStorage
} from "@/app/infrastructures/misc/Utils";
import { FlagPermissionUrgentDeliveryManagement } from "@/feature-flags/flag-permission-urgent-management";
import { FlagsPermissionBulkPrint } from "@/feature-flags/flag-bulk-print";
import { assertRelabelPrintLimit, relabelPrintLimit } from "./modules/relabel-print";
import type { RelabelPrintRequest } from "./modules/relabel-print-runtime";

export interface UrgentDeliveryManagementState {
  isLoading: boolean;
  PODList: UrgentDeliveryManagementPODData[];
  RTSList: UrgentDeliveryManagementRTSData[];
  HALList: UrgentDeliveryManagementHALData[];
  paginationData: Pagination;
  lastLocationOptions: LastLocationOption[];
}

@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "urget-delivery-management"
})
class UrgentDeliveryManagementStore extends VuexModule
  implements UrgentDeliveryManagementState {
  public isLoading = false;
  public PODList: UrgentDeliveryManagementPODData[] = [];
  public RTSList: UrgentDeliveryManagementRTSData[] = [];
  public HALList: UrgentDeliveryManagementHALData[] = [];
  public paginationData = new Pagination(1, 20, 100);
  public sttInfo: number = JSON.parse(
    localStorage.getItem("sttInfo") || "{}"
  );
  public PODSummary = new UrgentDeliveryManagementPODSummaryData();
  public lastLocationOptions: LastLocationOption[] = [];

  get isAccountForeign() {
    return AccountController.accountData.accountIsForeign;
  }

  @Action
  public getPODList(params: UrgentDeliveryManagementPODListRequest): void {
    MainAppController.closeErrorMessage();
    this.setLoading(true);

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    presenter
      .getPODList(params)
      .then(res => {
        this.setPODListData(res);
      })
      .catch(err => {
        const message = err.response.data.message;
        MainAppController.showErrorMessage(
          typeof message === "string"
            ? new ErrorMessageEntities({
                title: this.isAccountForeign
                  ? "Failed to Get POD List"
                  : "Gagal Mengambil Data POD",
                message: err.response.data.message
              })
            : parsingErrorResponse(err, this.isAccountForeign
              ? "Failed to Get POD List"
              : "Gagal Mengambil Data POD")
        );
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public downloadUrgentPODList(params: UrgentDeliveryManagementPODListRequestDownload): void {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    presenter
      .downloadUrgentPODList(params)
      .catch((err) => {
        MainAppController.showErrorMessage(
          new ErrorMessageEntities({
            title: this.isAccountForeign ? "Failed to Download POD List" : "Gagal Mengunduh Data POD",
            message: err.response.data.message
          })
        );
      })
      .finally(() => MainAppController.closeLoading());
  }

  @Action
  public getPODSummary(params: UrgentDeliveryManagementPODSummaryRequest): void {
    MainAppController.closeErrorMessage();
    this.setLoading(true);

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    presenter
      .getPODSummary(params)
      .then(res => {
        this.setPODSummaryData(res);
      })
      .catch(err => {
        const message = err.response.data.message;
        MainAppController.showErrorMessage(
          typeof message === "string"
            ? new ErrorMessageEntities({
                title: "Gagal Mengambil Data POD Summary",
                message: err.response.data.message
              })
            : parsingErrorResponse(err, "Gagal Mengambil Data POD Summary")
        );
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  @Action
  public getRTSList(params: UrgentDeliveryManagementRTSListRequest): void {
    MainAppController.closeErrorMessage();
    this.setLoading(true);

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    presenter
      .getRTSList(params)
      .then(res => {
        this.setRTSListData(res);
      })
      .catch(err => {
        MainAppController.showErrorMessage(
          new ErrorMessageEntities({
            title: this.isAccountForeign ? "Failed to Get RTS List" : "Gagal Mengambil Data RTS",
            message: err.response.data.message
          })
        );
      })
      .finally(() => this.setLoading(false));
  }

  @Action
  public downloadUrgentRTSList(params: UrgentDeliveryManagementRTSListRequestDownload): void {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    presenter
      .downloadUrgentRTSList(params)
      .catch((err) => {
        MainAppController.showErrorMessage(
          new ErrorMessageEntities({
            title: this.isAccountForeign ? "Failed to Download RTS List" : "Gagal Mengunduh Data RTS",
            message: err.response.data.message
          })
        );
      })
      .finally(() => MainAppController.closeLoading());
  }

  @Action
  public getHALList(params: UrgentDeliveryManagementHALListRequest): void {
    MainAppController.closeErrorMessage();
    this.setLoading(true);

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    presenter
      .getHALList(params)
      .then(res => {
        this.setHALListData(res);
      })
      .catch(err => {
        MainAppController.showErrorMessage(
          new ErrorMessageEntities({
            title: this.isAccountForeign ? "Failed to Get HAL List" : "Gagal Mengambil Data HAL",
            message: err.response.data.message
          })
        );
      })
      .finally(() => this.setLoading(false));
  }

  @Action
  public downloadUrgentHALList(params: UrgentDeliveryManagementHALListRequestDownload): void {
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    presenter
      .downloadUrgentHALList(params)
      .catch((err) => {
        MainAppController.showErrorMessage(
          new ErrorMessageEntities({
            title: this.isAccountForeign ? "Failed to Download HAL List" : "Gagal Mengunduh Data HAL",
            message: err.response.data.message
          })
        );
      })
      .finally(() => MainAppController.closeLoading());
  }

  @Action
  public getUrgentDeliveryManagementTakenRecipient(params: UrgentDeliveryManagementHALListRequest) {
    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    return presenter.getUrgentDeliveryManagementTakenRecipient(params);
  }

  @Action
  public getPriorityDeliveryManagementDetail(id: number) {
    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    return presenter.getPriorityDeliveryManagementDetail(id);
  }


  @Mutation
  private setLoading(bool: boolean): void {
    this.isLoading = bool;
  }

  @Mutation
  public setSttInfo(sttInfo: any) {
    this.sttInfo = sttInfo;
    storeDatatoLocalStorage("sttInfo", sttInfo);
  }

  @Mutation
  private setLastLocationOptions(options: LastLocationOption[]): void {
    this.lastLocationOptions = options;
  }

  @Mutation
  private clearLastLocationOptions(): void {
    this.lastLocationOptions = [];
  }

  @Mutation
  private setPODListData(data: UrgentDeliveryManagementPODList): void {
    this.PODList = data.data;
    this.paginationData = data.pagination;
  }

  @Mutation
  private setPODSummaryData(data: UrgentDeliveryManagementPODSummaryData): void {
    this.PODSummary = data
  }

  @Mutation
  private setRTSListData(data: UrgentDeliveryManagementRTSList): void {
    this.RTSList = data.data;
    this.paginationData = data.pagination;
  }

  @Mutation
  private setHALListData(data: UrgentDeliveryManagementHALList): void {
    this.HALList = data.data;
    this.paginationData = data.pagination;
  }

  @Action
  public async getLastLocationOptions(): Promise<void> {
    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    try {
      const options = await presenter.getLastLocationOptions();
      this.setLastLocationOptions(options);
    } catch (err) {
      // Silent failure — 18026 is contract-only, HTTP not wired yet.
    }
  }

  @Action
  public resetLastLocationOptions(): void {
    this.clearLastLocationOptions();
  }

  // Kept out of PODList/RTSList/HALList shared state (own request/response
  // cycle per detailing) so switching tabs never clobbers another tab's
  // data or pagination.
  @Action
  public getRelabelList(
    params: UrgentDeliveryManagementRelabelListRequest
  ): Promise<UrgentDeliveryManagementRelabelList> {
    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    return presenter.getRelabelList(params);
  }

  @Action
  public async downloadUrgentRelabelList(
    params: UrgentDeliveryManagementRelabelListRequestDownload
  ): Promise<void> {
    if (!FlagPermissionUrgentDeliveryManagement.permission_urgent_delivery_management_relabel_enable.isEnabled()) return;
    MainAppController.showLoading();
    MainAppController.closeErrorMessage();

    const presenter = container.resolve(UrgentDeliveryManagementPresenter);
    return presenter
      .downloadUrgentRelabelList(params)
      .catch((err) => {
        MainAppController.showErrorMessage(
          new ErrorMessageEntities({
            title: this.isAccountForeign
              ? "Failed to Download Relabel List"
              : "Gagal Mengunduh Data Relabel",
            message: err?.response?.data?.message || "Gagal mengunduh data. Silakan coba lagi."
          })
        );
      })
      .finally(() => MainAppController.closeLoading());
  }

  @Action
  public async canPrintRelabel(total: number): Promise<boolean> {
    if (!FlagPermissionUrgentDeliveryManagement.permission_urgent_delivery_management_relabel_enable.isEnabled()) return false;
    try {
      assertRelabelPrintLimit(total, relabelPrintLimit(FlagsPermissionBulkPrint.config_max_stt_input_bulk_print.getValue()));
      return total > 0;
    } catch (error) {
      MainAppController.showErrorMessage(new ErrorMessageEntities({
        title: "Batas Print Relabel", message: error.message
      }));
      return false;
    }
  }

  @Action
  public async printRelabel(request: RelabelPrintRequest): Promise<void> {
    if (!FlagPermissionUrgentDeliveryManagement.permission_urgent_delivery_management_relabel_enable.isEnabled()) return;
    MainAppController.closeErrorMessage();
    MainAppController.showLoading();
    try {
      const { printRelabel } = await import("./modules/relabel-print-runtime");
      const failed = await printRelabel(request);
      if (failed.length) throw new Error(`STT gagal dicetak: ${failed.join(", ")}`);
    } catch (error) {
      MainAppController.showErrorMessage(new ErrorMessageEntities({
        title: "Print Relabel", message: error?.message || "Gagal memproses print resi."
      }));
    } finally {
      MainAppController.closeLoading();
    }
  }
}

export const UrgentDeliveryManagementController = getModule(
  UrgentDeliveryManagementStore
);
